import { Link, router } from "@inertiajs/react";
import { useState } from "react";
import { BookOpen, ChevronRight, FileText, MapPin, Wand2 } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";

interface CandidateOption {
  id: number;
  full_name: string;
  firm_name: string;
  practice_area: string;
}

interface CandidateDetail {
  id: number;
  full_name: string;
  firm_name: string;
  practice_area: string;
  jd_year: number | null;
  location: string;
  candidate_type: string;
  law_school: string;
  specialties: string;
  notes: string;
  skm_status: string;
  resume_url: string | null;
  pipeline_stage: string;
}

interface ExistingBlast {
  id: number;
  status: string;
  profile_generated: boolean;
  anonymized_profile: string;
  contact_count: number;
  sent_at: string | null;
}

interface Props {
  candidates: CandidateOption[];
  selected: CandidateDetail | null;
  existing_blast: ExistingBlast | null;
  ai_assist: boolean;
}

const STAGE_LABELS: Record<string, string> = {
  intake_scheduled: "Intake Scheduled",
  resume_editing:   "Resume Editing",
  package_pending:  "Package Pending",
  submitted:        "Submitted",
  screener:         "Screener",
  full_round:       "Full Round",
};

export default function SkmGenerator({ candidates, selected, existing_blast, ai_assist }: Props) {
  const [candidateId, setCandidateId] = useState<string>(selected ? String(selected.id) : "");
  const [creating, setCreating]       = useState(false);

  function handleCandidateChange(id: string) {
    setCandidateId(id);
    if (id) {
      router.get("/skm/generate", { candidate_id: id }, { replace: true });
    } else {
      router.get("/skm/generate", {}, { replace: true });
    }
  }

  function startBlast() {
    if (!selected) return;
    setCreating(true);
    router.post("/skm/generate", { candidate_id: selected.id }, {
      onFinish: () => setCreating(false),
    });
  }

  return (
    <AppLayout title="SKM Generator">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SKM Generator</h1>
          <p className="text-sm text-gray-500 mt-0.5">Anonymized profile blast to target firms</p>
        </div>
        <Link href="/skm" className="text-sm text-gray-500 hover:text-gray-700">
          Contact List →
        </Link>
      </div>

      {/* Candidate selector */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Candidate</label>
        {candidates.length === 0 ? (
          <p className="text-sm text-gray-400">No candidates eligible for SKM. Add candidates to the pipeline first.</p>
        ) : (
          <select
            value={candidateId}
            onChange={(e) => handleCandidateChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-green-500"
          >
            <option value="">— Select a candidate —</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name} · {c.firm_name} · {c.practice_area}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Candidate summary */}
      {selected && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">{selected.full_name}</h2>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded capitalize">
                  {STAGE_LABELS[selected.pipeline_stage] ?? selected.pipeline_stage}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">{selected.firm_name}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Practice</p>
                  <p className="text-sm text-gray-800 mt-0.5">{selected.practice_area || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">JD Year</p>
                  <p className="text-sm text-gray-800 mt-0.5">{selected.jd_year ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Level</p>
                  <p className="text-sm text-gray-800 mt-0.5 capitalize">{selected.candidate_type || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Location</p>
                  <p className="text-sm text-gray-800 mt-0.5 flex items-center gap-1">
                    {selected.location ? <><MapPin className="w-3 h-3 shrink-0" />{selected.location}</> : "—"}
                  </p>
                </div>
              </div>

              {selected.specialties && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Specialties</p>
                  <p className="text-sm text-gray-600 mt-0.5">{selected.specialties}</p>
                </div>
              )}

              {selected.notes && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Notes</p>
                  <p className="text-sm text-gray-600 mt-0.5 line-clamp-3">{selected.notes}</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 items-end ml-4 shrink-0">
              {selected.resume_url && (
                <a href={selected.resume_url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs font-medium text-blue-700 border border-blue-200 hover:bg-blue-50 rounded px-2.5 py-1.5">
                  <FileText className="w-3.5 h-3.5" /> View Resume
                </a>
              )}
              <Link href={`/candidates/${selected.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 border-2 border-gray-400 bg-white hover:bg-gray-50 hover:border-gray-600 rounded-md px-2.5 py-1.5 transition-colors">
                <BookOpen className="w-3.5 h-3.5" /> Full Profile
              </Link>
            </div>
          </div>

          {/* Existing blast notice */}
          {existing_blast && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-amber-700">
                Draft SKM in progress
                {existing_blast.profile_generated && " · profile generated"}
                {existing_blast.contact_count > 0 && ` · ${existing_blast.contact_count} contacts`}
              </div>
              <Link href={`/skm/blasts/${existing_blast.id}`}
                className="flex items-center gap-1 text-sm font-medium text-green-700 hover:text-green-800">
                Continue draft <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* Start button */}
          {!existing_blast && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Button onClick={startBlast} disabled={creating}
                className="bg-green-700 hover:bg-green-800 text-white">
                <Wand2 className="w-4 h-4 mr-1.5" />
                {creating ? "Creating..." : "Generate SKM"}
              </Button>
              {!ai_assist && (
                <p className="text-xs text-gray-400 mt-2">
                  AI profile generation requires AI Assist — enable it in Settings.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!selected && candidates.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 py-16 text-center">
          <Wand2 className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Select a candidate above to begin the SKM flow.</p>
        </div>
      )}
    </AppLayout>
  );
}
